import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import StatusBadge from '../components/ui/StatusBadge';

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const downloadInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/orders/${id}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      if (!res.ok) throw new Error('Failed to generate invoice');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${order?.order_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Impossible de télécharger la facture.");
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/orders/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to fetch order');
        const data = await res.json();
        setOrder(data.order);
        setItems((data.order && data.order.items) ? data.order.items : []);
      } catch (e) {
        console.error(e);
        navigate('/orders');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const fmt = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

  if (loading) return (
    <div className="min-h-screen bg-gray-900 py-12 mt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 animate-pulse">
          <div className="h-7 w-72 bg-gray-700 rounded mb-2" />
          <div className="h-4 w-40 bg-gray-700 rounded" />
        </div>
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6 animate-pulse">
          <div className="grid grid-cols-2 gap-4">
            <div className="h-4 bg-gray-700 rounded" />
            <div className="h-4 bg-gray-700 rounded" />
            <div className="h-4 bg-gray-700 rounded" />
            <div className="h-4 bg-gray-700 rounded" />
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 animate-pulse">
          <div className="h-5 w-40 bg-gray-700 rounded mb-4" />
          <div className="space-y-3">
            {[...Array(3)].map((_,i)=> <div key={i} className="h-10 bg-gray-700 rounded" />)}
          </div>
        </div>
      </div>
    </div>
  );
  if (!order) return null;

  return (
    <div className="min-h-screen bg-gray-900 py-12 mt-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Order Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 mb-8 shadow-2xl">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Section - Order Info */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white mb-1">Commande #{order.order_number}</h1>
                </div>
              </div>
              
              {/* Order Meta Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                  <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Date & Heure</div>
                  <div className="text-white font-medium text-sm">
                    {new Date(order.created_at).toLocaleDateString('fr-FR', { 
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="text-gray-300 text-xs">
                    {new Date(order.created_at).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                  <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Total</div>
                  <div className="text-yellow-400 font-bold text-lg">{fmt(order.total_amount)}</div>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                  <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Articles</div>
                  <div className="text-white font-medium">{items.length} produit{items.length > 1 ? 's' : ''}</div>
                </div>
              </div>
            </div>

            {/* Right Section - Actions */}
            <div className="flex flex-col items-end justify-end">
              {/* Action Buttons */}
              <div className="flex flex-col gap-2 mt-auto">
                <button
                  onClick={downloadInvoice}
                  className="group relative px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-gray-900 font-medium text-sm rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg shadow-md flex items-center gap-2"
                >
                  <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Télécharger facture PDF
                </button>
                
                <Link 
                  to="/orders" 
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium text-sm rounded-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-2 justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour aux commandes
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Résumé de la commande</h2>
            <StatusBadge status={order.status} size="default" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="text-gray-300">Paiement: <span className="text-white">{order.payment_status}</span></div>
            <div className="text-gray-300">Total: <span className="text-yellow-400 font-semibold text-lg">{fmt(order.total_amount)}</span></div>
            {order.payer_email && <div className="text-gray-300">PayPal: <span className="text-white">{order.payer_email}</span></div>}
          </div>
        </div>

        {/* Status Progress Indicator */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Progression de la commande</h2>
          <div className="flex items-center justify-between">
            {['pending', 'confirmed', 'processing', 'shipped', 'delivered'].map((status, index) => {
              const isCompleted = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'].indexOf(order.status) >= index;
              const isCurrent = order.status === status;
              
              return (
                <div key={status} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                    isCompleted ? 'bg-green-500' : 'bg-gray-600'
                  }`}>
                    {isCompleted ? (
                      <span className="text-white text-sm">✓</span>
                    ) : (
                      <span className="text-gray-400 text-sm">{index + 1}</span>
                    )}
                  </div>
                  <div className={`text-xs text-center ${
                    isCurrent ? 'text-yellow-400 font-semibold' : isCompleted ? 'text-green-400' : 'text-gray-500'
                  }`}>
                    {status === 'pending' ? 'En attente' :
                     status === 'confirmed' ? 'Confirmée' :
                     status === 'processing' ? 'En traitement' :
                     status === 'shipped' ? 'Expédiée' :
                     status === 'delivered' ? 'Livrée' : status}
                  </div>
                  {index < 4 && (
                    <div className={`w-16 h-0.5 mt-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-600'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Articles</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400">
                <th className="text-left py-2">Produit</th>
                <th className="text-right py-2">Qté</th>
                <th className="text-right py-2">Prix</th>
                <th className="text-right py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx} className="text-gray-300">
                  <td className="py-1">
                    <div className="flex items-center gap-3">
                      {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover rounded" onError={(e)=>{e.target.style.display='none';}} />}
                      <span>{it.product_name || it.name}</span>
                    </div>
                  </td>
                  <td className="text-right py-1">{it.quantity}</td>
                  <td className="text-right py-1">{fmt(it.unit_price || it.price)}</td>
                  <td className="text-right py-1">{fmt(it.total_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-3">Adresse de livraison</h3>
            <div className="text-gray-300">
              <div>{order.shipping_address || '—'}</div>
              <div>{order.shipping_postal_code} {order.shipping_city}</div>
              <div>{order.shipping_country}</div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-3">Adresse de facturation</h3>
            <div className="text-gray-400">Identique à la livraison</div>
          </div>
        </div>
      </div>
    </div>
  );
}


